import { OPERATORS } from '../../constants';
import { BaseOperatorStrategy } from './base.operator-strategy';
import type {
  SelectionBlockerArgs,
  SelectionBlockerResult,
  UnitCostCalculationArgs,
} from '../operator-strategy';

/**
 * Voyage Radar (aisvesseltracker) — AIS credit-wallet operator.
 *
 * Pricing model: unitcost × apiCredits, where `unitcost` is the USD price of
 * ONE API credit (operator doc `costunit: "credit"`) and `apiCredits` is the
 * wallet the user buys in the operator popup. Every spendable action (a vessel
 * refresh, a vessel track) then debits that one wallet by its catalogue cost.
 * No holdback / cloud-cover / ONA factors apply, and userCost is deliberately
 * not applied either — this override bypasses the base-class factor math.
 *
 * Constraints:
 *  - Open-water targets only (AIS is maritime data).
 *  - Recurrent orders are not supported.
 *  - Area AND point targets are served — a point refresh queries the operator's
 *    nearby endpoint over a fixed radius — but only ONE point per task, and
 *    only while the operator document says points are offered.
 */
export class VoyageRadarOperatorStrategy extends BaseOperatorStrategy {
  readonly operatorKey = OPERATORS.VOYAGERADAR;

  /**
   * Set unitcost from the sensor row's latency tier and selectedCost to
   * unitcost × apiCredits.
   */
  override calculateUnitCost({ sensorRow }: UnitCostCalculationArgs): void {
    if (!sensorRow) return;

    const unitPrice = Number(sensorRow.unitcost ?? sensorRow.latency?.[0]?.unitcost ?? 0);
    // `?? 1`, not `?? 0`: before the popup runs a zero would zero out
    // latency[0].cost and the tier dropdown would render "Not Offered".
    const credits = Number(sensorRow.apiCredits ?? 1);

    if (Array.isArray(sensorRow.latency) && sensorRow.latency[0]) {
      sensorRow.latency[0].cost     = unitPrice;
      sensorRow.latency[0].unitcost = unitPrice;
    }
    sensorRow.unitcost     = unitPrice;
    sensorRow.selectedCost = unitPrice * credits;
  }

  protected computeBaseCost(sensorRow: any, latencyIndx: number): number | null {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return null;
    const unitcost = Number(sensorRow.latency[latencyIndx]?.unitcost ?? 0);
    const credits  = Number(sensorRow.apiCredits ?? 1);
    return unitcost * credits;
  }

  /**
   * Selection blockers (evaluated in order):
   *  1. Point targets, when the operator document does not offer them.
   *  2. Multi-point targets — never supported, see below.
   *  3. AIS is open-water only — block land targets.
   *  4. Recurrent orders are not supported for AIS refresh operators.
   *
   * Target shape is checked first: it decides whether this operator can serve
   * the order at all, which is more fundamental than where the target sits or
   * how often it repeats.
   */
  getSelectionBlocker(args: SelectionBlockerArgs): SelectionBlockerResult | null {
    const { sensorRow, taskingContext } = args;

    if (taskingContext?.isPointTarget) {
      // Driven by the operator document, never by the operator key: ops can
      // turn point support off in ee-admin, and the server then drops this
      // operator from a point quote entirely. `!== true` so an absent flag
      // reads as "not offered", matching how the backend resolves it.
      if (sensorRow?.poi !== true) {
        return this.createPoiNotSupportedBlocker(sensorRow, 'VOYAGERADAR_POI_UNSUPPORTED');
      }

      // A refresh queries exactly ONE disc around the tasked point, so a second
      // point would be silently unserved and still billed at the same credit
      // cost. The server agrees — the operator carries no `multi_poi` flag, so
      // both the sensor-list gate and the quote drop it from a multi-point
      // request — but a user should be told here rather than finding the
      // operator quietly missing.
      if ((taskingContext.multiTargetCount ?? 0) > 1) {
        return {
          code: 'VOYAGERADAR_MULTI_POI_UNSUPPORTED',
          // Neutral noun when the name is absent — `displayName` is already
          // obfuscation-aware and its absence must not leak the real name.
          message:
            `${sensorRow?.displayName ?? 'This sensor'} covers one point of interest per task. ` +
            'Please select a single point, switch to an area, or choose an alternative provider.',
        };
      }
    }

    if ((taskingContext as any)?.openWater === false) {
      return {
        code:    'VOYAGERADAR_OPEN_WATER_ONLY',
        // Falls back to a neutral noun, not the operator name — `displayName`
        // is already obfuscation-aware, and its absence must not leak the name.
        message: `${sensorRow?.displayName ?? 'This sensor'} AIS tracking is only available over open water. Please adjust your area of interest.`,
      };
    }

    if (taskingContext?.isRecurrent) {
      return this.createUnsupportedRecurrencyBlocker(
        sensorRow,
        'VOYAGERADAR_RECURRENCY_UNSUPPORTED',
      );
    }

    return null;
  }

  /**
   * Stated here because the operator document has nothing to state it with: the
   * doc carries `cancel_policy: []` (cancellation is auto-approved in
   * cancel.route.js, refunds handled by refundOptions.js), and the generic
   * "Cancelation <window> : <penalty>" rendering of an empty table produces an
   * empty tooltip. This sentence IS the policy for this operator.
   */
  getCancellationPolicyText(): string {
    return 'No cancelation is permitted once credits are purchased';
  }
}
